import axios from "axios";
import Cookies from "js-cookie";
import { keys } from "@/lib/utils/keys";
import { constants } from "@/lib/utils/constants";
import { apiBaseUrl } from "@/lib/services/backend";
import { getCustomerAxios } from "@/lib/utils/axios";
import { logError } from "@/lib/utils/errors/logger";
import {
  handleGraphqlResponse,
  transformSearchResponse,
  returnSearchRequestSentCtx,
} from "@/lib/utils/functions";

export const searchProductsAndCategories = async ({
  searchText,
  pageSize = 8,
  currentPage = 1,
  catPageSize = 10,
  catCurrentPage = 1,
  specialCoupon,
  userViewHistoryGraphQL = [],
}) => {
  try {
    if (!userViewHistoryGraphQL.length) {
      const userViewHistory = Cookies.get(
        constants.cookies.searchProductClicked,
      )
        ? JSON.parse(Cookies.get(constants.cookies.searchProductClicked))
        : [];
      userViewHistoryGraphQL = userViewHistory
        .map((item) => `{ sku: "${item.sku}", dateTime: "${item.dateTime}" }`)
        .join(", ");
    }
    const startTime = performance.now();
    const textWithoutQuotes = searchText?.replaceAll('"', "");
    const query = `query {
      productSearch (
        phrase: "${textWithoutQuotes}"
        filter: [
          { attribute: "price", range: { from: 0.01 } }
          {attribute: "visibility", in: ["Search", "Catalog, Search"]}
        ]
        context: {
          customerGroup: "${process.env.NEXT_PUBLIC_CUSTOMER_GROUP}"
          userViewHistory: [${userViewHistoryGraphQL}]
        }
        page_size: ${pageSize}
        promo_offer_show:1
        current_page: ${currentPage}
        cate_page_size: ${catPageSize}
        cate_current_page: ${catCurrentPage}
        special_coupon: "${specialCoupon || ""}"
      ) {
        request_id total_count special_coupon_applied suggestions related_terms
        search_trems { query_text redirect }
        page_info {
          current_page
          page_size total_pages cate_page_size cate_total_count cate_current_page
        }
        categories {
          id name link image
        }
        items {
          productView {
            breadcrumbs art_nr externalId best_seller is_recommended sold_in_pairs warranty_guaranteed_text stop_start_compatible inStock sku name urlKey
            brand_info { id title image brand_tier }
            images { url label }
            promo_offer { label promo_image }
            attributes (roles: []) {
              name label value
            }
            price {
              final {
                amount { value currency }
              }
              regular {
                amount { value currency }
              }
            }
          }
        }
      }
    }`;
    const axiosRes = await axios.post(apiBaseUrl(), { query });
    const endTime = performance.now();
    const executionTime = (endTime - startTime).toFixed(2);
    const res = handleGraphqlResponse(axiosRes.data);
    const inputSearchContext = returnSearchRequestSentCtx({
      searchText,
      pageSize,
      currentPage,
      request_id: res?.productSearch?.request_id,
    });
    return {
      data: transformSearchResponse(
        res,
        executionTime,
        currentPage,
        pageSize,
        inputSearchContext,
      ),
      error: null,
    };
  } catch (error) {
    logError({
      error,
      info: { searchText, specialCoupon },
      message: "searchProductsAndCategories",
    });
    return { data: [], error: error.message };
  }
};

export const notifyStockAlertMail = async (product_id) => {
  try {
    const currentAxios = await getCustomerAxios();
    const axiosRes = await currentAxios.post(keys.general.backendGraphqlUrl, {
      query: `mutation ProductAlertStock($input: ProductAlertInput!) {
        productAlertStock (input: $input) {
          status
        }
      }`,
      variables: { input: { product_id } },
    });
    const res = handleGraphqlResponse(axiosRes);
    let err;
    if (res?.errors) {
      err = res?.errors[0].message;
    } else {
      err = null;
    }
    return { data: res.data, error: err };
  } catch (error) {
    logError({ error, info: { product_id }, message: "notifyStockAlertMail" });
    return { data: null, error };
  }
};

export const unsubscribeNotifyStockAlertMail = async () => {
  try {
    const currentAxios = await getCustomerAxios();
    const axiosRes = await currentAxios.post(keys.general.backendGraphqlUrl, {
      query: `mutation { unSubscribeAllProductStock { status } }`,
    });
    const res = handleGraphqlResponse(axiosRes);
    let err;
    if (res?.errors) {
      err = res?.errors[0].message;
    } else {
      err = null;
    }
    return { data: res.data, error: err };
  } catch (error) {
    logError({ error, message: "unsubscribeNotifyStockAlertMail" });
    return { data: null, error };
  }
};

export const notifyStockAlertMailGuest = async (payload) => {
  try {
    const axiosRes = await axios.post(apiBaseUrl(), {
      query: `mutation GuestProductAlertStock($input: GuestProductAlertInput!) {
        guestProductAlertStock (input: $input) { status }
      }`,
      variables: { input: payload },
    });
    const res = handleGraphqlResponse(axiosRes);
    let err;
    if (res?.errors) {
      err = res?.errors[0].message;
    } else {
      err = null;
    }
    return { data: res.data, error: err };
  } catch (error) {
    logError({
      error,
      info: { payload },
      message: "notifyStockAlertMailGuest",
    });
    return { data: null, error };
  }
};

export const unsubscribeNotifyStockAlertMailGuest = async (payload) => {
  try {
    const axiosRes = await axios.post(apiBaseUrl(), {
      query: `mutation UnSubscribeGuestProductAlertStock($input: GuestProductAlertUnsubscribeInput!) {
        unSubscribeGuestProductAlertStock(input: $input) {
          status email
        }
      }`,
      variables: { input: payload },
    });
    const res = handleGraphqlResponse(axiosRes);
    let err;
    if (res?.errors) {
      err = res?.errors[0].message;
    } else {
      err = null;
    }
    return { data: res.data, error: err };
  } catch (error) {
    logError({
      error,
      info: { payload },
      message: "unsubscribeNotifyStockAlertMailGuest",
    });
    return { data: null, error };
  }
};
